/**
 * **全站注册路由的真名单**：`App.tsx` 里每一个 `<Route path=...>` 的路径模式。
 *
 * ## 为什么它必须是一个**可导入的常量**，而不是散落在 App.tsx 里的字符串
 *
 * 因为"返回目标"必须真的存在。2026-10 的生产事故就出在这里：
 * `/students/:id`（孩子详情）的返回目标被写成了 `/students` —— 一个**语义上
 * 完全说得通、路由表里却从来没有过**的路径（孩子列表其实是 `/`）。
 * `react-router` 的 `<Link>` 与地址栏一样要过路由匹配表，匹配不到就落 `*` 兜底，
 * 于是用户在手机上点左上角「←」，直接看到了「页面不存在」。
 *
 * 那次事故 `tsc` 全绿、49 条单测全绿：因为**没有任何一处断言"目标页真的存在"**。
 * 现在：
 *   - 这个常量是名单的唯一出处，`App.tsx` 的每条路由都用它（见 `routePath()`）；
 *   - `backNav.ts` 的 `BACK_ROWS[].parent` 只允许指向名单里的路径；
 *   - `backNav.test.ts` 会 ①逐条核对每个返回目标都在名单里、
 *     ②从 **`App.tsx` 源码**里抽出真实 `path=` 并与名单**双向**比对
 *     （少了 → 名单过时；多了 → 有人加了路由没登记）。
 *
 * ⚠️ `'*'` 是兜底路由（渲染成「页面不存在」），**不是**可用的返回目标，故不在此列。
 */

/** 带动态段的路径用 `:名字` 表示，与 react-router 的写法一致。 */
export const REGISTERED_ROUTE_PATHS = [
  // ---------- 未登录可见 ----------
  '/login',
  '/register',
  '/register/verify',
  '/forgot',
  '/reset',
  // ---------- 家长端 ----------
  '/',
  '/students/:id',
  '/students/:id/points',
  '/students/:id/nodes',
  '/students/:id/homework',
  '/students/:id/rewards',
  '/students/:id/redemptions',
  '/students/:id/stats',
  /**
   * 「工具」：**孩子们共用**的东西放这里（2026-10-14 导航重构）。
   *
   * 用户原话："我觉得语文听写这一块，应该把数据录入等孩子们共用的部分提取出来，
   * 放在一个外部的地方……我觉得可以把'共享'写成'工具'，而'听写'属于一个工具，
   * 以后可能会有其他的工具，比如英语的听写等等。"
   *
   * 于是 `/tools` 取代了原来顶层导航里的 `/shares`，`/tools/dictation` 取代了
   * `/students/:id/dictation`（见下）。
   */
  '/tools',
  /**
   * 语文听写（第二步：录入界面）—— **顶层工具页**。
   *
   * ⚠️ 后端 21 个听写端点**全部**挂在 `/students/:id/dictation/...`，用
   *   `resolveStudentAccess(app, principal, :id)` 判权限，**没有全局的
   *   `/dictation/*`**。所以这一页虽然不依赖路径里的孩子，也必须**在内部挑一个
   *   孩子**发请求（`?anchor=`，见 `ParentDictationPage` 与 `lib/dictationAnchor.ts`）。
   *   数据本身是全家的（听写 6 张表里没有 `student_id`）。
   *
   * ⚠️ 但这是**后端约束，不是家长的概念**：界面上**不提**"锚点"，也没有选孩子的
   *   控件；"按哪个年级取词"只在选词预览里让家长直接选**年级**。横幅只说
   *   "这套字表是全家人共用的"（2026-10 用户原话："这一块的「锚点」的作用我不理解"）。
   */
  '/tools/dictation',
  /**
   * 语文听写（第三步：听写设置 / 布置听写 / 做听写）—— **挂在孩子下面**。
   *
   * ## 为什么它与 `/tools/dictation` 是两件事（用户明确要求过）
   *
   * 用户原话："针对每一个小孩的听写设置、听写布置以及听写任务的布置，
   * 就可以放在某个具体的孩子下面。"
   *
   * | 界面 | 路径 | 数据 |
   * | --- | --- | --- |
   * | **数据录入**（课本/课文/生字/词语） | `/tools/dictation` | 全家共用（表里没有 `student_id`） |
   * | **听写设置 / 布置 / 做听写 / 历史** | `/students/:id/dictation` | **这个孩子的**（会话表有 `student_id`） |
   *
   * ⚠️ 上一轮这里**故意空着**：`StudentDetailPage` 里留了一段注释说明
   * "本轮不留一个点了没用的按钮"。这一步把它填上了。
   */
  '/students/:id/dictation',
  /** 做听写（播放器）：按节奏读 N 遍 + 间隔，可暂停/重播/跳过。 */
  '/students/:id/dictation/:sessionId',
  /**
   * 看一次听写的全部题目 + **家长批改**（第四步）。
   *
   * ⚠️ 它与下面那条 `mistakes` 的段数**不同**（5 段 vs 6 段），所以不会互相吃掉。
   *   `mistakes` 是 5 段（`students / :id / dictation / mistakes`），与
   *   `':sessionId'` 是**同一个位置**的静态段 vs 动态段 —— Fastify 与
   *   react-router 都是**静态优先**，所以 `/dictation/mistakes` 一定命中错词本。
   *   这条由 `dictation.routes-order.test.ts` 显式验（本项目已经为
   *   `words/pick` vs `words` 做过同一件事）。
   */
  '/students/:id/dictation/:sessionId/review',
  /**
   * **预习卡**（田字格 + 拼音 + 笔顺动画）：在"开始播放"之前用。
   *
   * 用户原话："列出来放在田字格里面，上面标注有拼音，最好能有笔画顺序的动画演示……
   * 这样孩子在听写之前，可以先预习看一下那个字怎么写。"
   *
   * ⚠️ 它与上面那条 `review` 段数相同（都是 6 段），第 5 段是静态的 `preview`
   *    vs `review` —— 静态段不会互相吃掉（`routes.ts` + `backNav.test.ts` 都盯着这件事）。
   *    上一级是**这次听写**（播放器那一页），因为预习完就回到那里点「开始播放」。
   */
  '/students/:id/dictation/:sessionId/preview',
  /**
   * 错词本（第四步）：按孩子看需要重听的词、筛"今天该复习的"、
   * 手动标"已掌握"、**直接从错词本开始一次听写**。
   */
  '/students/:id/dictation/mistakes',
  '/rewards',
  '/settings',
  // ---------- 学生端 ----------
  '/student',
  '/student/homework',
  /**
   * 语文听写（孩子端，迁移 0020 的批量任务）。
   *
   * 用户原话："孩子端要有听写入口 —— 这样就不用大人陪着。"
   *
   *   · `/student/dictation` —— 家长布置的任务 + 「开始听写」；
   *   · `/student/dictation/:sessionId` —— 这一批的播放器（「提交这一批」）；
   *   · `/student/dictation/:sessionId/preview` —— 预习卡（田字格 + 拼音 + 笔顺）。
   *
   * 2026-10-28 用户补充："家长端……右上角有一个「先预习」的功能。
   * 我觉得孩子那边也要加一个这个入口。" → 预习也挂到孩子端，
   * 与家长端共用 `DictationPreviewPanel`。
   *
   * ⚠️ 三条路径段数互不相同（3 / 4 / 5 段），不会互相吃掉；
   *    静态段 `dictation` 与参数段 `:sessionId` 也不冲突。
   */
  '/student/dictation',
  '/student/dictation/:sessionId',
  '/student/dictation/:sessionId/preview',
  '/student/points',
  '/student/rewards',
  '/student/settings',
  // ---------- 系统管理后台 ----------
  '/admin/login',
  '/admin',
  '/admin/users',
  /**
   * 「听写字表」（2026-10-16 新增）：字表维护的**唯一入口**。
   *
   * 用户原话："这个工具类应该……不是家长负责维护，而应该是站点负责维护……
   * 录入维护这一块应该由系统管理员去做，应该放到系统管理员那里去。"
   *
   * 于是 `/tools/dictation` 从"录入界面"改成"只读 + 取词"，而
   * `/admin/dictation` 接管 21 条维护端点（课本/课文/字/词/收录/导入/拼音纠音）。
   *
   * ⚠️ 路径必须在 `/admin` 下面：Caddy 在 `/api/v1/admin/*` 上做了
   *    "仅内网 / SSH 隧道来源"的限制（见 deploy/Caddyfile），
   *    而这个页面对应的后端接口全在 `/admin/dictation/*`。
   *    （该限制**线上尚未生效**，见 docs/RUNBOOK.md §5.4；
   *    路径放这里的理由是"语义上属于管理面"，不依赖它是否生效。）
   */
  '/admin/dictation',
  '/admin/settings',
  '/admin/audit',
  '/admin/mail',
  // ---------- 兜底页 ----------
  '/404',
] as const;

/** 注册路由模式（联合类型）。 */
export type RegisteredRoutePath = (typeof REGISTERED_ROUTE_PATHS)[number];

const REGISTERED = new Set<string>(REGISTERED_ROUTE_PATHS);

/**
 * 在 `App.tsx` 里给 `<Route path>` 用的取值函数。
 *
 * 作用是把"路径必须是注册过的"这件事变成**编译期**错误：`path={routePath('/xxxx')}`
 * 里的字符串不是名单成员时，`tsc --noEmit` 直接报错（不需要等测试跑）。
 * 顺带让 `App.tsx` **真的 import 并执行**这个名单，从而不可能出现
 * "名单改了一套、App.tsx 写的是另一套"还全绿的情况。
 */
export function routePath(path: RegisteredRoutePath): RegisteredRoutePath {
  // 运行期再挡一次：绕过类型（any / 动态拼接）时也不会静默通过。
  if (!REGISTERED.has(path)) throw new Error(`未注册的路由路径：${path}`);
  return path;
}
